import { FormProps } from 'types/form';
import { ChangeEvent, ReactNode } from 'react';
import OWForm from 'components/layout/ow-form';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OWSelect from 'components/design/ow-select';
import FormFooter from 'components/layout/form-footer';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'utils/object-helpers';
import { NestedProductCategory } from '../types/dtos';
import { useFilterSelectStatusOptions, useFilterSelectIsMultiSelectOptions } from '../constants/status';

export type NestedProductCategoryFormProps = FormProps<NestedProductCategory> & {
    onCheckChange: (event: ChangeEvent<HTMLInputElement>) => void;
    isUnlimited: boolean;
};

type NestedProductCategotyFormType = (props: NestedProductCategoryFormProps) => ReactNode;

export const NestedProductCategoryForm: NestedProductCategotyFormType = ({
    formData: nestedProductCategory,
    onChange,
    onSubmit,
    onCancel,
    onCheckChange,
    isUnlimited,
    isSaveLoading,
    errors,
    disableSubmit,
}) => {
    const { t } = useTranslation();
    const FILTER_SELECT_OPTIONS_STATUS = useFilterSelectStatusOptions();
    const FILTER_SELECT_OPTIONS_MULTI_SELECT = useFilterSelectIsMultiSelectOptions();

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h6">{t('NESTED_PRODUCT_CATEGORY_FORM.HEADER')}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack width="100%" spacing={1.5}>
                    <TextField
                        value={nestedProductCategory.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('NESTED_PRODUCT_CATEGORY_FORM.PLACEHOLDER_NAME')}
                        label={t('NESTED_PRODUCT_CATEGORY_FORM.LABEL_NAME')}
                        required
                        autoFocus
                        error={!!errors?.name}
                        helperText={errors?.name?.errorMessage}
                    />
                    <OWSelect
                        value={nestedProductCategory.isActive.toString()}
                        items={FILTER_SELECT_OPTIONS_STATUS}
                        label={t('NESTED_PRODUCT_CATEGORY_FORM.LABEL_STATUS')}
                        labelId="label-status"
                        error={!!errors?.isActive}
                        onChange={(e) => onChange('isActive', e.target.value)}
                    />
                    <Stack direction="row" width="100%" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                            <OWSelect
                                value={nestedProductCategory.isMultiSelect.toString()}
                                items={FILTER_SELECT_OPTIONS_MULTI_SELECT}
                                label={t('NESTED_PRODUCT_CATEGORY_FORM.LABEL_MULTI_SELECT')}
                                labelId="label-status"
                                error={!!errors?.isMultiSelect}
                                onChange={(e) => onChange('isMultiSelect', e.target.value)}
                            />
                        </Box>

                        {nestedProductCategory.isMultiSelect === 'YES' && (
                            <>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        height: '100%',
                                        minHeight: 56,
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox checked={isUnlimited} onChange={onCheckChange} />}
                                        label={t('NESTED_PRODUCT_CATEGORY_FORM.LABEL_UNLIMITED')}
                                        sx={{
                                            margin: 0,
                                            '& .MuiFormControlLabel-label': {
                                                marginLeft: 1,
                                            },
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        value={isUnlimited ? '' : nestedProductCategory.maxSelection}
                                        onChange={(e) => onChange('maxSelection', e.target.value)}
                                        placeholder={t('NESTED_PRODUCT_CATEGORY_FORM.PLACEHOLDER_MAX_SELECTION')}
                                        label={t('NESTED_PRODUCT_CATEGORY_FORM.LABEL_MAX_SELECTION')}
                                        required={!isUnlimited}
                                        error={!!errors?.maxSelection}
                                        helperText={errors?.maxSelection?.errorMessage}
                                        disabled={isUnlimited}
                                    />
                                </Box>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>

            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('PRODUCT_CATEGORY_FORM.CANCEL_BUTTON')}
                    </Button>
                    <LoadingButton
                        loading={isSaveLoading}
                        type="submit"
                        disabled={
                            !isEmpty(errors) ||
                            disableSubmit ||
                            (nestedProductCategory.isMultiSelect === 'YES' &&
                                isUnlimited === false &&
                                nestedProductCategory.maxSelection === '')
                        }
                    >
                        {t('PRODUCT_CATEGORY_FORM.SAVE_BUTTON')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
